import React, { useContext, useEffect, useState } from 'react';
import { TABTestItem } from '../../types/ab';
import { TPaginationParams } from 'Pages/GMS/Performance/analytics';
import { createAbTest, deleteABTest, getABTests } from '../../services/ABServices';
import { AppContext } from 'Context/AppContext';
import TableSkeletonElement from 'Components/GMS/Products/components/TableSkeletonElement';
import ABTable from '../../components/ABTable';
import { toast } from 'react-toastify';
import DeleteProjectModal from 'Pages/GMS/ISR/Components/DeleteProjectModal';
import Paginator from 'Components/Common/PaginatorWithApi';
import { CREATE_AB_TEST, GMS_AB_TESTING_CREATE, GMS_AB_TESTING_DELETE, GMS_AB_TESTING_READ, GMS_AB_TESTING_UPDATE, TEST_NAME } from '../../utils/Constants';
import { useHistory } from 'react-router-dom';
import NoDataComponent from 'Components/Common/NoDataComponent';
import ABHomeHeader from './ABHomeHeader';
import { ABContext } from '../../contexts/ABContext';
import CreateABTestDialog from '../../components/CreateABTestDialog';

const ABHomePage = () => {
    const history = useHistory();
    const appContext = useContext(AppContext);
    const abContext = useContext(ABContext);
    const industryId = appContext.get_selected_industry() && appContext?.get_selected_industry()?.id;
    const channelKey = appContext?.channelKey
    const [abTestData, setAbTestData] = useState<TABTestItem[]>([]);
    const [paginationParams, setPaginationParams] = useState<TPaginationParams>({ pageNumber: 1, pageSize: 10 });
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [deleteItem, setDeleteItem] = useState<{ id: string, name: string }>({ id: '', name: '' });

    const [showConfirmDialog, setDeleteTest] = useState<boolean>(false);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [showIndicator, setShowIndicator] = useState<boolean>(false);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [showDialog, setShowDialog] = useState<boolean>(false);

    const readPermission = appContext.pm.checkPermission(GMS_AB_TESTING_READ);
    const createPermission = appContext.pm.checkPermission(GMS_AB_TESTING_CREATE);
    const updatePermission = appContext.pm.checkPermission(GMS_AB_TESTING_UPDATE);
    const deletePermission = appContext.pm.checkPermission(GMS_AB_TESTING_DELETE);

    const openDialog = () => {
        if (!createPermission) { history.push("/workspace/module/permission/denied"); return; }
        setShowDialog(true);
    }
    const closeDialog = () => setShowDialog(false);

    useEffect(() => {
        if (!readPermission) { history.push("/workspace/module/permission/denied"); return; }
        if (industryId && channelKey) {
            getAllTests();
            const intervalId = setInterval(getAllTests, 60 * 1000);
            return () => clearInterval(intervalId);
        }
    }, [industryId, channelKey, paginationParams]);

    const getAllTests = async () => {
        setShowLoader(true);
        try {
            const responseData = await getABTests(industryId ?? '', appContext?.channelKey ?? 'test', paginationParams);
            if (responseData?.done) {
                setTotalRecords(responseData?.total_tests ?? 10);
                const abTests = responseData?.data ?? [];
                if (abTests.length === 0 && paginationParams?.pageSize === 20 && paginationParams?.pageNumber > 1) {
                    setPaginationParams(prevState => ({
                        ...prevState,
                        pageNumber: 1
                    }));
                    return;
                }
                setAbTestData(abTests);

            } else {
                toast.error(responseData?.message)
            }
        } catch (error) {
            console.log(error, 'Error');
        } finally {
            setShowLoader(false);
        }
    }

    const createABTest = async (testName: string) => {
        abContext.clearData();
        try {
            setShowIndicator(true);
            const responseData = await createAbTest(industryId ?? '', appContext?.channelKey, testName);
            if (responseData?.done) {
                navigateToDetailPage(responseData?.data?.name ?? '', responseData?.data?.id ?? '');
            } else {
                toast.error(responseData?.message ?? '');
            }
        } catch (error) {
            console.log("error:", error)
        } finally {
            setShowIndicator(false);
        }
    }

    const deleteTestData = async () => {
        if (!deleteItem?.id) {
            toast.info('Test id is missing');
            return;
        }
        setDeleteLoading(true);
        try {
            const responseData = await deleteABTest(deleteItem?.id ?? '');
            if (responseData?.done) {
                toast.success(responseData?.message);
                setPaginationParams({ pageNumber: 1, pageSize: 10 });
            } else {
                toast.error(responseData?.message);
            }
        } catch (error) {
            console.log('Error', error);
        } finally {
            setDeleteLoading(false);
            setDeleteTest(false);
        }
    }

    const closeDeleteModal = () => {
        setDeleteTest(false);
    };

    const handlePageChange = (pageNumber: number) => {
        setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageNumber: pageNumber,
        }));
    }

    const updatePageSize = (pageSize: number) => {
        setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageSize: pageSize,
        }));
    };

    const navigateToDetailPage = (name: string, testId: string = '') => {
        if (!updatePermission) { history.push("/workspace/module/permission/denied"); return; }
        history.push(`/gms/ab/detail/${testId}`);
    }

    return (
        <div>
            <ABHomeHeader onOpen={openDialog} hasCreatePermission={createPermission} />
            {showLoader ? <TableSkeletonElement /> : null}
            {!showLoader && abTestData?.length > 0 ? <ABTable abTests={abTestData} setDeleteItem={setDeleteItem} setDeleteTest={setDeleteTest} deletePermission={deletePermission} updatePermission={updatePermission} /> : <div>{!showLoader ? <NoDataComponent /> : null}</div>}
            {!showLoader && abTestData?.length > 0 ?
                <div className="tw-py-2">
                    <div className={`tw-flex tw-justify-end tw-items-center tw-space-x-3 tw-flex-wrap`}>
                        <div className={`tw-flex tw-items-center tw-space-x-3`}>
                            <span>Show</span>
                            <select className={`tw-border focus:tw-outline-none tw-p-1`} style={{ width: "80px" }} aria-label="Records on page select" value={paginationParams?.pageSize ?? 10} onChange={(e) => { updatePageSize(parseInt(e?.target?.value)); }}>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                            </select>
                        </div>
                        <Paginator onPageChange={handlePageChange}
                            recordPerPage={paginationParams?.pageSize}
                            presentPage={paginationParams?.pageNumber}
                            totalRecords={totalRecords} />
                    </div>
                </div>
                : null}
            {showDialog ? <CreateABTestDialog dialogHeader={CREATE_AB_TEST} labelName={TEST_NAME} onClose={closeDialog} onSubmodelFieldSubmit={createABTest} showIndicator={showIndicator} message='Creating AB test ...' /> : null}
            <DeleteProjectModal showModal={showConfirmDialog} closeModal={closeDeleteModal} loading={deleteLoading} onConfirm={deleteTestData} deleteItem={deleteItem} />
        </div>
    );
}

export default ABHomePage;